/**
 * ViewCounter — licznik REALNYCH odsłon (zasada: ZERO FAKE KPI).
 *
 * Wyświetla wyłącznie prawdziwe dane z page_views (przez backend).
 * Gdy realna liczba = 0 → nic nie renderujemy (bez „social proof", bez zmyślania).
 *
 * Props:
 *   - totalViews   — realna liczba z DB / usePageViews (0 = brak danych)
 *   - lastView     — ISO timestamp ostatniej odsłony (z backend)
 *   - variant      — "detail" (pełny, z label) | "card" (mały, tylko ikonka + liczba)
 *   - className    — override stylowania
 */
import { Eye, Clock } from 'lucide-react';
import { useMemo } from 'react';

function formatRelativeTime(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const now = Date.now();
    const diffSec = Math.max(0, Math.round((now - then) / 1000));
    if (diffSec < 60) return 'przed chwilą';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min temu`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} godz. temu`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} dni temu`;
    return `${Math.floor(diffSec / 604800)} tyg. temu`;
  } catch {
    return null;
  }
}

export default function ViewCounter({
  totalViews = 0,
  lastView = null,
  variant = 'detail',
  className = '',
}) {
  const displayCount = Number(totalViews) || 0;
  const relTime = useMemo(() => formatRelativeTime(lastView), [lastView]);

  if (variant === 'card') {
    if (displayCount <= 0) return null;
    return (
      <span
        className={`inline-flex items-center gap-1 text-[11px] text-gray-400 ${className}`}
        title={`${displayCount} wyświetleń`}
        data-testid="view-counter-card"
      >
        <Eye className="w-3 h-3" />
        {displayCount.toLocaleString('pl-PL')}
      </span>
    );
  }

  if (displayCount <= 0 && !relTime) return null;

  return (
    <div
      className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400 ${className}`}
      data-testid="view-counter-detail"
    >
      {displayCount > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5 text-gray-500" />
          <span>
            <strong className="text-gray-200 font-semibold">{displayCount.toLocaleString('pl-PL')}</strong>{' '}
            wyświetleń
          </span>
        </span>
      )}
      {relTime && (
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          {displayCount > 0 && <span className="text-gray-600">·</span>}
          <Clock className="w-3 h-3" />
          Ostatnio oglądano: <span className="text-gray-300">{relTime}</span>
        </span>
      )}
    </div>
  );
}
